import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertOctagon, GitBranch, GitBranchPlus, GitMerge, List, Info, Server, Pause, Play, Sliders, KeyRound, Link as LinkIcon, Search, Trash2, RotateCcw, Gauge } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';

const POLL_INTERVAL_OPTIONS = [
  { value: 60, label: '60 seconds' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
];

const formatPollInterval = (seconds?: number): string => {
  if (!seconds) return '5 minutes';
  if (seconds % 60 === 0) {
    const mins = seconds / 60;
    return mins === 1 ? '1 minute' : `${mins} minutes`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
};

const NeonIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleRepo, setExampleRepo] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);
  const [pollInterval, setPollInterval] = useState(300);
  const [organization, setOrganization] = useState('');

  const [apiKey, setApiKey] = useState('');
  const [connectOrg, setConnectOrg] = useState('');
  const [connectPollInterval, setConnectPollInterval] = useState(300);

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getNeonStatus();
      if (data?.connected) {
        setConnected(true);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
        setPollInterval(data.poll_interval || 300);
        setOrganization(data.org_id || '');
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectNeon({
        api_key: apiKey,
        org_id: connectOrg || undefined,
        poll_interval: connectPollInterval,
      });
      setSuccessMsg('Neon connected successfully!');
      setApiKey('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectNeon();
      setConnected(false);
      setSuccessMsg('Neon disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testNeon();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Neon (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={neonIcon} alt="Neon" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Neon
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Serverless Postgres branch lifecycle, compute suspend/resume and autoscaling, reset-from-parent, credential rotation, and investigation automation</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {organization && (
                <div>
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium">{organization}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Resources Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()} resource{resourcesTracked !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Poll Interval</span>
                <p className="font-medium">{formatPollInterval(pollInterval)}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Neon actions and triggers in your workflows to automate ephemeral preview databases, CI/CD, cost control, and incident response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (poll-based)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Branch Created', desc: 'Triggers when a new Neon branch is created.', Icon: GitBranchPlus },
                  { name: 'Branch Ready', desc: 'Triggers when a branch becomes ready to connect.', Icon: GitBranch },
                  { name: 'Operation Failed', desc: 'Triggers when a Neon operation enters a failed state.', Icon: AlertOctagon },
                  { name: 'Compute Suspended', desc: 'Triggers when a compute endpoint autosuspends (cost).', Icon: Pause },
                  { name: 'Compute Active', desc: 'Triggers when a compute endpoint resumes to active.', Icon: Play },
                  { name: 'Usage/Quota Threshold', desc: 'Triggers when a project crosses a usage threshold.', Icon: Gauge },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'List Projects', desc: 'List projects with region and pg version.', Icon: List },
                  { name: 'Get Project', desc: 'Fetch a project\u2019s region and version.', Icon: Info },
                  { name: 'List Branches', desc: 'List a project\u2019s branches.', Icon: List },
                  { name: 'Get Branch', desc: 'Fetch a branch\u2019s state.', Icon: Info },
                  { name: 'Create Branch', desc: 'Instant copy-on-write branch (+ optional compute).', Icon: GitBranchPlus },
                  { name: 'Delete Branch', desc: 'Tear down a branch (gate behind Approval).', Icon: Trash2 },
                  { name: 'Reset Branch from Parent', desc: 'Reset a branch to its parent\u2019s state.', Icon: RotateCcw },
                  { name: 'List Compute Endpoints', desc: 'List a project\u2019s compute endpoints.', Icon: List },
                  { name: 'Create Compute Endpoint', desc: 'Provision a compute endpoint on a branch.', Icon: Server },
                  { name: 'Delete Compute Endpoint', desc: 'Delete a compute endpoint (gate behind Approval).', Icon: Trash2 },
                  { name: 'Suspend Compute', desc: 'Idle a compute endpoint to cut cost.', Icon: Pause },
                  { name: 'Start Compute', desc: 'Resume a suspended compute endpoint.', Icon: Play },
                  { name: 'Set Autoscaling Limits', desc: 'Update autoscaling min/max compute units.', Icon: Sliders },
                  { name: 'Rotate Role Credentials', desc: 'Reset a role password + return a fresh URI.', Icon: KeyRound },
                  { name: 'Get Connection URI', desc: 'Fetch a branch connection string.', Icon: LinkIcon },
                  { name: 'Investigate Neon', desc: 'AI investigates projects, branches, endpoints, operations.', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a pull request opens in{' '}
              <input
                value={exampleRepo}
                onChange={e => setExampleRepo(e.target.value)}
                placeholder="org/repo"
                style={{ width: `${Math.max(exampleRepo.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />, create a Neon copy-on-write preview branch and post the connection URI to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack. When the PR closes, delete the branch.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleRepo || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a pull request opens in ${exampleRepo}, create a Neon copy-on-write preview branch and post the connection URI to ${exampleChannel} on Slack. When the PR closes, delete the branch.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={neonIcon} alt="Neon" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Neon</CardTitle>
                <CardDescription>Connect Neon for serverless Postgres branch lifecycle, compute suspend/resume and autoscaling, reset-from-parent, credential rotation, and investigation automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">API Key</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to Neon with an <strong className="text-foreground">API key</strong>, which it sends to the Neon API at <code className="bg-muted px-1 rounded">console.neon.tech/api/v2</code> to read and manage projects, branches, compute endpoints, and operations. Create one in the Neon Console (<code className="bg-muted px-1 rounded">console.neon.tech</code>) under <strong className="text-foreground">Account settings → API keys → Create new API key</strong> (a personal or organization key). The key is shown only once.
              </p>
              <Input
                id="neon-key"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="napi_..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Neon API.
              </p>
            </div>
            <div>
              <Label htmlFor="neon-org" className="text-sm">Organization ID (optional)</Label>
              <Input
                id="neon-org"
                value={connectOrg}
                onChange={e => setConnectOrg(e.target.value)}
                placeholder="org-..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Required if your Neon account is organization-scoped (a personal account can leave this blank). Find your org ID in the Neon Console URL when you open the org (<code className="bg-muted px-1 rounded">console.neon.tech/app/<strong className="text-foreground">org-...</strong>/projects</code>), or under <strong className="text-foreground">Organization settings → Settings</strong>.
              </p>
            </div>
            <div>
              <Label htmlFor="neon-poll" className="text-sm">Poll Interval</Label>
              <select
                id="neon-poll"
                value={connectPollInterval}
                onChange={e => setConnectPollInterval(Number(e.target.value))}
                className="mt-1 w-full sm:w-auto block rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {POLL_INTERVAL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Neon has no control-plane webhooks, so Kestrel polls the Neon API to detect branch, compute, operation, and usage events. Faster intervals detect events sooner; the minimum is 60 seconds.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !apiKey}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Neon
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default NeonIntegration;
